import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Delete } from "lucide-react";
import { setSession, verifyPin } from "@/lib/api";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/35918463/pexels-photo-35918463.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pin.length === 4) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const press = (k) => {
    if (k === "back") setPin((p) => p.slice(0, -1));
    else if (pin.length < 6) setPin((p) => p + k);
  };

  const submit = async (value) => {
    setBusy(true);
    try {
      const r = await verifyPin(value);
      setSession(value, r.role, r.name || "");
      toast.success(r.role === "owner" ? "Welcome back" : r.name ? `Hi ${r.name}` : "Signed in");
      nav("/", { replace: true });
    } catch {
      toast.error("Wrong PIN");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const keys = ["1","2","3","4","5","6","7","8","9","","0","back"];

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} aria-hidden />
      <div className="absolute inset-0 bg-[#FDFBF7]/85 backdrop-blur-sm" aria-hidden />
      <div className="relative flex-1 flex flex-col px-6 py-10 max-w-md mx-auto w-full">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">Aguasabon Golf Club</div>
            <h1 className="text-3xl text-[#0D311E] mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
              Till Trax
            </h1>
          </div>
          <span data-testid="draft-badge" className="inline-flex items-center rounded-full bg-[#D19C4C]/20 px-2.5 py-1 text-[10px] font-semibold text-[#A76A1F] uppercase tracking-[0.18em]">
            Draft
          </span>
        </div>

        <div className="mt-16 mb-8 text-center">
          <p className="text-[#2B533E] text-base">Enter your PIN</p>
          <p className="text-[#5C7566] text-xs mt-1">Your own PIN, the shared staff PIN, or the owner PIN</p>
        </div>

        <div data-testid="pin-display" className="flex justify-center gap-3 mb-10">
          {[0,1,2,3].map((i) => (
            <div
              key={i}
              className={`h-4 w-4 rounded-full border-2 ${pin.length > i ? "bg-[#1A5D3A] border-[#1A5D3A]" : "bg-transparent border-[#5C7566]/40"}`}
            />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto w-full">
          {keys.map((k, idx) => {
            if (k === "") return <div key={idx} />;
            if (k === "back") {
              return (
                <button
                  key={idx}
                  data-testid="pin-back"
                  onClick={() => press("back")}
                  className="h-16 rounded-2xl bg-[#F5F2EA]/80 border border-[#E8E4DA] flex items-center justify-center active:scale-95"
                >
                  <Delete size={20} className="text-[#2B533E]" />
                </button>
              );
            }
            return (
              <button
                key={idx}
                data-testid={`pin-key-${k}`}
                onClick={() => press(k)}
                disabled={busy}
                className="h-16 rounded-2xl bg-white border border-[#E8E4DA] text-2xl text-[#0D311E] shadow-sm active:bg-[#F5F2EA] active:scale-95 disabled:opacity-50"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                {k}
              </button>
            );
          })}
        </div>

        <div className="mt-auto pt-10 text-center text-[#5C7566] text-xs">
          Nobody's in trouble for an honest note.
        </div>
      </div>
    </div>
  );
}
